import React from "react";

const footerStyles = `
:root { --footer-max: 1200px; --accent: #fbbf24; --muted: #cbd5e1; --bg-1: #071024; --bg-2: #0b1220; }

.app-footer { 
  position: relative; 
  background: linear-gradient(180deg,var(--bg-2),var(--bg-1)); 
  color: #fff; 
  padding: 48px 20px 32px 20px; 
  width: 100%; 
  box-sizing: border-box; 
  font-family: 'Inter', sans-serif;
}

.footer-inner { max-width: var(--footer-max); margin: 0 auto; padding: 0 18px; }

.footer-accent { 
  position: absolute; left: 0; right: 0; top: 0; 
  height: 1px; width: 100%; 
  background: var(--accent); 
}

/* Top Section (Columns) */
.footer-top { 
  display: flex; 
  gap: 32px; 
  align-items: flex-start; 
  justify-content: space-between; 
  flex-wrap: wrap; /* Critical for Zoom/Mobile */
}

.footer-col { 
  flex: 1 1 240px; /* Allow growing, shrinking, basis of 240px */
  min-width: 0; /* CSS Grid/Flex fix */
}

.footer-logo { display:flex; align-items:center; gap:14px; margin-bottom: 16px; }
.footer-logo img { width:64px; height:64px; border-radius:10px; object-fit:cover; box-shadow: 0 6px 18px rgba(0,0,0,0.5); }
.brand-title { font-size:20px; font-weight:800; color:var(--accent); margin:0; }
.brand-tag { color:var(--muted); margin-top:6px; font-size:14px; line-height:1.35; }

.footer-h3 { color:var(--accent); margin:0 0 12px 0; font-size:17px; font-weight:700; text-transform: uppercase; letter-spacing: 0.05em; }

.footer-links { display:flex; flex-direction:column; gap:10px; }
.footer-links a { color:var(--muted); text-decoration:none; font-size:14px; transition: color 0.2s; }
.footer-links a:hover { color: #fff; text-decoration: underline; }

.footer-contact { color:var(--muted); font-size:14px; line-height: 1.6; }

.footer-divider { height:1px; background: rgba(255,255,255,0.1); margin: 32px 0; }

/* Bottom Section */
.footer-bottom { 
  display: flex; 
  flex-wrap: wrap; /* Allows wrapping on zoom */
  justify-content: space-between; 
  align-items: center; 
  gap: 24px; 
}

.bottom-left { flex: 1 1 auto; }
.bottom-right { 
  display: flex; 
  gap: 16px; 
  align-items: center; 
  flex-wrap: wrap; 
}

.copyright { color: rgba(255,255,255,0.6); font-size:13px; }
.dev-credit { color: rgba(255,255,255,0.6); font-size:13px; margin-top: 4px; }

.bottom-links { display:flex; gap:20px; align-items:center; flex-wrap: wrap; }
.bottom-links a { color: var(--muted); font-size:13px; text-decoration:none; }
.bottom-links a:hover { color: #fff; text-decoration: underline; }

.scroll-top { 
  width:44px; height:44px; 
  border-radius:50%; 
  background:var(--accent); 
  display:inline-flex; align-items:center; justify-content:center; 
  box-shadow: 0 4px 12px rgba(0,0,0,0.3); 
  border: none; cursor: pointer; 
  flex-shrink: 0;
  transition: transform 0.2s;
}
.scroll-top:hover { transform: translateY(-2px); }

/* Responsive adjustments */
@media (max-width: 768px) { 
  .footer-top { flex-direction: column; gap: 40px; } 
  .footer-col { width: 100%; flex: auto; }
  .footer-bottom { flex-direction: column-reverse; align-items: flex-start; gap: 32px; }
  .bottom-right { width: 100%; justify-content: space-between; }
  .scroll-top { position: absolute; bottom: 32px; right: 20px; }
}
`;

const Footer = ({ navigate }) => {
  const scrollToTop = () => window.scrollTo({ top: 0, behavior: "smooth" });

  const navTo = (e, target, fallbackPath) => {
    if (e && typeof e.preventDefault === "function") e.preventDefault();
    if (typeof navigate === "function") return navigate(target);
    if (fallbackPath) window.location.href = fallbackPath;
  };

  return (
    <footer className="app-footer" aria-label="Site footer">
      <div className="footer-accent" />
      <style>{footerStyles}</style>
      <div className="footer-inner">
        <div className="footer-top">
          <div className="footer-col">
            <div className="footer-logo">
              <img src="/logo.png" alt="Lextria logo" />
              <div>
                <div className="brand-title">Lextria Research</div>
                <div className="brand-tag">
                  Where innovation meets effortless Documentation.
                </div>
              </div>
            </div>
          </div>

          <div className="footer-col">
            <h4 className="footer-h3">Navigate</h4>
            <nav className="footer-links" aria-label="Footer navigation">
              <a href="/login" onClick={(e) => navTo(e, "login", "/login")}>
                Login
              </a>
              <a href="/" onClick={(e) => navTo(e, "about", "/")}>
                About
              </a>
            </nav>
          </div>

          <div className="footer-col">
            <h4 className="footer-h3">Contact Us</h4>
            <div className="footer-contact">
              <div>
                <a
                  href="mailto:info@lextria.in"
                  style={{ color: "var(--muted)", textDecoration: "none" }}
                >
                  info@lextria.in
                </a>
              </div>
              <div style={{ marginTop: 8 }}>Bangalore, India</div>
            </div>
          </div>
        </div>

        <div className="footer-divider" />

        <div className="footer-bottom">
          <div className="bottom-left">
            <div className="copyright">
              © 2024 Lextria Research. All rights reserved.
            </div>
            <div className="dev-credit">
              Developed and maintained by Sigma Solutions
            </div>
          </div>

          <div className="bottom-right">
            <div className="bottom-links">
              <a
                href="/privacy-policy"
                onClick={(e) => navTo(e, "privacy", "/privacy-policy")}
              >
                Privacy Policy
              </a>
              <a
                href="/terms-of-use"
                onClick={(e) => navTo(e, "terms", "/terms-of-use")}
              >
                Terms of Use
              </a>
              <a
                href="/disclaimer"
                onClick={(e) => navTo(e, "disclaimer", "/disclaimer")}
              >
                Disclaimer
              </a>
            </div>
            <button
              aria-label="Scroll to top"
              className="scroll-top"
              onClick={scrollToTop}
            >
              <svg
                width="20"
                height="20"
                viewBox="0 0 24 24"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M7 14l5-5 5 5"
                  stroke="#071024"
                  strokeWidth="2.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </button>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;